import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { AuthenticationContext } from '../contexts/authentication.context';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ZiroModalService } from '../services/ziro-modal.service';
import { EulaModalComponent, EulaModalViewProperties } from './eula-modal.component';
import { HtmlSanitizerService } from '../services/html-sanitizer.service';

@Injectable()
export class EulaContext implements OnDestroy {
  private _http = inject(HttpClient);
  private _authenticationContext = inject(AuthenticationContext);
  private _ziroModalService = inject(ZiroModalService);
  private _htmlSanitizerService = inject(HtmlSanitizerService);

  private EULA_TOPIC_ID = 79;
  private _eulaUrl = `https://learn.goziro.com/t/${this.EULA_TOPIC_ID}/posts.json`;
  private _subscriptions = new Subscription();
  private _isEulaModalOpen = false;

  _stateSource = new ReplaySubject<void>();
  state$ = this._stateSource.asObservable();

  constructor() {
    const sub = this._authenticationContext.state$.subscribe((user) => {
      if (user.userId) {
        this._checkForNewEula(user.eulaAcceptanceDate);
      }
    });

    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _getRawEulaPost(): Observable<any> {
    return this._http.get(this._eulaUrl);
  }

  private _setAcceptanceDate(): Observable<void> {
    return this._http.post<void>('/services/system/security/current-user/eula-signature', {}).pipe(
      tap(() => {
        this._isEulaModalOpen = false;
      }),
    );
  }

  private _checkForNewEula(userAcceptanceDate: string): void {
    const sub = this._getRawEulaPost()
      .pipe(
        switchMap((rawEula: any) => this._getEulaDetails(rawEula)),
        switchMap((eula: EulaModalViewProperties) => this._displayEulaModal(eula, userAcceptanceDate)),
        switchMap((isModalAccepted) => {
          if (isModalAccepted) {
            return this._setAcceptanceDate();
          }
        }),
        catchError(() => {
          this._stateSource.next();
          return of(null);
        }),
      )
      .subscribe(() => this._stateSource.next());

    this._subscriptions.add(sub);
  }

  private _displayEulaModal(eula: EulaModalViewProperties, userAcceptanceDate: string): Observable<boolean> {
    if (!this._isEulaModalOpen && (!userAcceptanceDate || userAcceptanceDate < eula.reviewDate)) {
      this._isEulaModalOpen = true;
      return this._ziroModalService
        .openCustomModal(
          EulaModalComponent,
          { html: eula.html, reviewDate: eula.reviewDate, slug: eula.slug },
          { size: 'lg' },
        )
        .pipe(tap(() => of(true)));
    } else {
      return of(false);
    }
  }

  private _getEulaDetails(rawEula: any): Observable<EulaModalViewProperties | null> {
    const post = rawEula.post_stream.posts[0];
    const reviewDateMatch = post.cooked.match(/\d{4}\.\d{2}\.\d{2}/);

    if (reviewDateMatch) {
      return of({
        html: this._htmlSanitizerService.sanitizeAndTrustHtml(post.cooked),
        reviewDate: reviewDateMatch[0].replaceAll('.', '-'),
        slug: post.topic_slug,
      });
    } else {
      return of(null);
    }
  }
}
